import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Globe2,
  Instagram,
  Linkedin,
  Youtube,
  Clock
} from "lucide-react";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PlaneGame from "../components/PlaneGame";
import SEO from "../components/SEO";
import SectionStepper from "../components/SectionStepper";
import { siteMeta } from "../data/mock";

// --- EmailJS Configuration ---
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const initialForm = { name: "", email: "", company: "", service: "", budget: "", message: "" };
const SERVICES = ["Film Making", "Video Editing", "Motion Graphics", "Web Development", "Branding", "Social Media", "Logo Designing", "Corporate AV Brochure", "Other"];
const BUDGETS = ["< ₹1L", "₹1L – ₹5L", "₹5L – ₹20L", "₹20L+"];

const ContactPage = () => {
  const [form, setForm] = useState(initialForm);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.4
    });

    let raf;
    const tick = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    window.lenis = lenis;
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }

    setSubmitting(true);

    // Prepare WhatsApp Message first to ensure it's ready
    const whatsappNumber = "919827555008";
    const message = `*Meraki Solutions - New Project Brief*%0A` +
      `---------------------------------------%0A` +
      `*Name:* ${form.name}%0A` +
      `*Email:* ${form.email}%0A` +
      `*Company:* ${form.company || "N/A"}%0A` +
      `*Service:* ${form.service || "N/A"}%0A` +
      `*Budget:* ${form.budget || "N/A"}%0A%0A` +
      `*Message:*%0A${form.message}`;

    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

    try {
      // 1. Attempt EmailJS (Silent backup)
      const templateParams = {
        from_name: form.name,
        from_email: form.email,
        company: form.company || "N/A",
        service: form.service || "N/A",
        budget: form.budget || "N/A",
        message: form.message,
        to_name: "Meraki Solutions Team",
      };

      if (EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY) {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          templateParams,
          EMAILJS_PUBLIC_KEY
        );
      }
    } catch (error) {
      console.warn("EmailJS Backup failed, proceeding to WhatsApp:", error);
    }

    // 2. Success handling & WhatsApp Redirection (Always happens)
    setSent(true);
    toast.success("Redirecting to WhatsApp for instant connection...");

    setTimeout(() => {
      window.open(whatsappUrl, "_blank");
      setForm(initialForm);
      setSent(false);
      setSubmitting(false);
    }, 1500);
  };

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO
        title="Contact Us"
        description="Get in touch with Meraki Solutions. Let's craft something unforgettable. Send us a brief for your film, branding, or digital project."
      />
      <div className="grain" />
      <Navbar />
      <main>
        <SectionStepper
          sections={[
            { id: "top", label: "Connect" },
            { id: "form", label: "Project Brief" },
            { id: "game", label: "Sky Drift" },
            { id: "map", label: "Studio" }
          ]}
        />
        {/* Hero Section */}
        <section id="top" className="relative pt-32 pb-20 overflow-hidden min-h-[60vh] flex items-center">
          {/* Background Video */}
          <div className="absolute inset-0 z-0">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.35 }}
              transition={{ duration: 1.5 }}
              className="w-full h-full"
            >
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="w-full h-full object-cover grayscale brightness-50"
              >
                <source src="https://videos.pexels.com/video-files/3129671/3129671-uhd_2560_1440_30fps.mp4" type="video/mp4" />
              </video>
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)]" />
          </div>
          <div className="container-xl w-full px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-sm mb-6 block">
                GET IN TOUCH
              </span>
              <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mb-10 leading-[0.9] tracking-tight">
                Let's craft <br />
                <span className="italic font-serif-accent gold-text">something</span> unforgettable.
              </h1>
              <p className="max-w-2xl text-[var(--cream-muted)] text-xl md:text-2xl font-body leading-relaxed">
                Whether it's a 30-second spot or a year-long brand transformation, we'd love to hear about it.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Content Section */}
        <section id="form" className="pb-32 relative">
          <div className="container-xl px-6">
            <div className="grid lg:grid-cols-12 gap-16 lg:gap-24">
              {/* Left Column: Contact Info & Cards */}
              <div className="lg:col-span-5 space-y-12">
                <div className="grid grid-cols-1 gap-6">
                  <ContactCard
                    icon={<Mail className="text-[var(--gold)]" size={24} />}
                    label="Email Us"
                    value={siteMeta.email}
                    href={`mailto:${siteMeta.email}`}
                  />
                  <ContactCard
                    icon={<Phone className="text-[var(--gold)]" size={24} />}
                    label="Call Us"
                    value={siteMeta.phone}
                  />
                  <ContactCard
                    icon={<MapPin className="text-[var(--gold)]" size={24} />}
                    label="Visit Studio"
                    value={siteMeta.location}
                  />
                </div>

                <div className="p-10 rounded-2xl bg-[var(--ink-2)] border border-[var(--rule)] relative overflow-hidden group">
                  <Clock className="absolute -top-4 -right-4 w-24 h-24 text-[var(--gold)] opacity-5 transform rotate-12 group-hover:rotate-0 transition-transform duration-700" />
                  <h3 className="font-display text-2xl mb-4 text-[var(--cream)]">Studio Hours</h3>
                  <div className="space-y-3 text-[var(--muted)] font-body">
                    <div className="flex justify-between border-b border-[var(--rule)] pb-2">
                      <span>Monday - Friday</span>
                      <span className="text-[var(--cream-muted)]">10:00 - 19:00</span>
                    </div>
                    <div className="flex justify-between border-b border-[var(--rule)] pb-2">
                      <span>Saturday</span>
                      <span className="text-[var(--cream-muted)]">11:00 - 16:00</span>
                    </div>
                    <div className="flex justify-between text-[var(--gold)] pt-2">
                      <span>Sunday</span>
                      <span className="font-impact tracking-wider">CLOSED</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <SocialLink icon={<Globe2 size={20} />} href={siteMeta.socials.website} />
                  <SocialLink icon={<Instagram size={20} />} href={siteMeta.socials.instagram} />
                  <SocialLink icon={<Linkedin size={20} />} href={siteMeta.socials.linkedin} />
                </div>
              </div>

              {/* Right Column: Contact Form */}
              <div className="lg:col-span-7">
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="p-8 md:p-12 rounded-3xl bg-[var(--ink-2)] border border-[var(--rule)] shadow-2xl relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--gold)] opacity-[0.03] rounded-full -translate-y-1/2 translate-x-1/2" />

                  <h2 className="font-display text-3xl md:text-4xl mb-10 text-[var(--cream)]">Send us a brief</h2>

                  <form onSubmit={onSubmit} className="space-y-8">
                    <div className="grid md:grid-cols-2 gap-8">
                      <InputField label="Your Name" name="name" value={form.name} onChange={onChange} required placeholder="John Doe" />
                      <InputField label="Email Address" name="email" type="email" value={form.email} onChange={onChange} required placeholder="john@example.com" />
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                      <InputField label="Company Name" name="company" value={form.company} onChange={onChange} placeholder="Acme Corp" />
                      <div className="space-y-2">
                        <label className="block text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">Service Interested In</label>
                        <select
                          name="service"
                          value={form.service}
                          onChange={onChange}
                          className="w-full bg-transparent border-b border-[var(--rule)] py-3 text-[15px] text-[var(--cream)] outline-none focus:border-[var(--gold)] appearance-none transition-colors"
                        >
                          <option value="" className="bg-[var(--ink)]">Select a service</option>
                          {SERVICES.map((s) => (
                            <option key={s} value={s} className="bg-[var(--ink)]">{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <label className="block text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">Project Budget Range</label>
                      <div className="flex flex-wrap gap-3">
                        {BUDGETS.map((b) => (
                          <button
                            type="button"
                            key={b}
                            onClick={() => setForm({ ...form, budget: b })}
                            className={`px-6 py-2 rounded-full border text-xs uppercase tracking-widest transition-all duration-300 ${form.budget === b
                              ? "bg-[var(--gold)] border-[var(--gold)] text-[var(--ink)]"
                              : "border-[var(--rule)] text-[var(--muted)] hover:border-[var(--gold)] hover:text-[var(--gold)]"
                              }`}
                          >
                            {b}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">Your Message *</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={onChange}
                        required
                        rows={4}
                        placeholder="Tell us about your project, goals, and timelines..."
                        className="w-full bg-transparent border-b border-[var(--rule)] py-3 text-[15px] text-[var(--cream)] outline-none focus:border-[var(--gold)] resize-none transition-colors placeholder:text-[var(--muted)]"
                      />
                    </div>

                    <div className="pt-4 flex flex-col md:flex-row items-center justify-between gap-8">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] flex items-center gap-2">
                        <Clock size={12} /> Typical reply time: &lt; 24 hours
                      </p>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="btn-primary min-w-[200px]"
                      >
                        {sent ? (
                          <span className="flex items-center gap-2"><CheckCircle2 size={18} /> Message Sent</span>
                        ) : submitting ? (
                          <span>Sending...</span>
                        ) : (
                          <span className="flex items-center gap-2">Send Brief <Send size={16} /></span>
                        )}
                      </button>
                    </div>
                  </form>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        <PlaneGame id="game" />
        {/* Map/Location Section Placeholder */}
        <section id="map" className="py-32 border-t border-[var(--rule)]">
          <div className="container-xl px-6">
            <div className="rounded-3xl overflow-hidden grayscale contrast-125 opacity-50 border border-[var(--rule)] relative aspect-[4/5] md:aspect-[21/9]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14666.126487103253!2d77.44708775!3d23.2237882!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x397c4273873426e9%3A0xc480415a772c7263!2sIndrapuri%2C%20Bhopal%2C%20Madhya%20Pradesh!5e0!3m2!1sen!2sin!4v1715265000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Studio Location"
              />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[var(--ink)] via-transparent to-[var(--ink)] opacity-60" />
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

const ContactCard = ({ icon, label, value, href }) => (
  <motion.div
    whileHover={{ x: 10 }}
    className="p-8 rounded-2xl bg-[var(--ink-2)] border border-[var(--rule)] flex items-center gap-6 group hover:border-[var(--gold)] transition-all duration-500"
  >
    <div className="w-14 h-14 rounded-full bg-[var(--ink)] border border-[var(--rule)] flex items-center justify-center flex-shrink-0 group-hover:border-[var(--gold)] transition-colors">
      {icon}
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] mb-1">{label}</div>
      {href ? (
        <a href={href} className="text-lg md:text-xl font-display text-[var(--cream)] hover:text-[var(--gold)] transition-colors">
          {value}
        </a>
      ) : (
        <div className="text-lg md:text-xl font-display text-[var(--cream)]">
          {value}
        </div>
      )}
    </div>
  </motion.div>
);

const InputField = ({ label, ...props }) => (
  <div className="space-y-2">
    <label className="block text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
      {label}{props.required ? " *" : ""}
    </label>
    <input
      {...props}
      className="w-full bg-transparent border-b border-[var(--rule)] py-3 text-[15px] text-[var(--cream)] outline-none focus:border-[var(--gold)] transition-colors placeholder:text-[var(--muted)]"
    />
  </div>
);

const SocialLink = ({ icon, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="w-12 h-12 rounded-full border border-[var(--rule)] flex items-center justify-center text-[var(--cream-muted)] hover:border-[var(--gold)] hover:text-[var(--gold)] hover:scale-110 transition-all duration-300"
  >
    {icon}
  </a>
);

export default ContactPage;
